import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BookOpenText, EnvelopeSimple, Lock } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Innova branded panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#071D35] via-[#123A63] to-[#2F6EA5]">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl"></div>
        <div className="absolute top-16 right-12 h-44 w-44 rounded-full border border-white/20 bg-white/5"></div>
        <div className="absolute left-16 top-1/3 h-24 w-24 rounded-full bg-[#FFD21E]/10 blur-lg"></div>
        <div className="absolute bottom-28 -right-16 h-64 w-64 rounded-full bg-[#5AA9E6]/20 blur-xl"></div>
        <div className="absolute bottom-20 left-20 h-36 w-36 rounded-full border border-[#FFD21E]/20"></div>
        <BookOpenText
          size={260}
          weight="duotone"
          className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 text-white/10"
          aria-hidden="true"
        />
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.35)_1px,transparent_1px)] [background-size:30px_30px]"></div>
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.28)_2px,transparent_2px)] [background-position:14px_18px] [background-size:76px_76px]"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 pt-24 text-white">
          <div className="mb-6 h-1.5 w-32 rounded-full bg-[#FFD21E]"></div>
          <h1 className="text-4xl font-bold mb-4 font-['Manrope']">Innova Training Learner Portal</h1>
          <p className="text-lg text-blue-50/90 max-w-md">Access your coursework and continue your progress.</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="/innova-logo.png"
              alt="Innova Training"
              className="mx-auto mb-6 h-auto w-64 max-w-full"
            />
            <h2 className="text-3xl font-bold text-slate-900 font-['Manrope']">
              Sign in to continue
            </h2>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Email
                </Label>
                <div className="relative">
                  <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-[#3B5A7A] focus:border-transparent"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-[#3B5A7A] focus:border-transparent"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#3B5A7A] hover:bg-[#2A4158] text-white font-medium"
                data-testid="login-submit-btn"
              >
                {loading ? 'Please wait...' : 'Sign In'}
              </Button>
            </form>
          </div>

          <p className="mt-5 text-center text-sm text-slate-500" aria-label="Having trouble signing in? Contact Innova Training">
            Having trouble signing in?{' '}
            <span className="font-semibold text-[#3B5A7A]">Contact Innova Training</span>
          </p>
        </div>
      </div>
    </div>
  );
}
